import { useCallback, useEffect, useState } from 'react'
import type { LoginResponse } from '../api/auth'
import {
  APPROVAL_LEVELS,
  APPROVAL_OPERATIONS,
  approvalMatrixApi,
  type ApprovalLevel,
  type ApprovalMatrixList,
  type ApprovalMatrixRule,
  type ApprovalMatrixRuleBody,
} from '../api/approvalMatrix'
import {
  Button,
  Callout,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  DataTable,
  EmptyState,
  FormGrid,
  IconButton,
  LoadingState,
  Page,
  PageHeader,
  Select,
  StatusPill,
  TextField,
  useConfirm,
} from '../components/ui'

/**
 * R3-27 (REQ-264-001) — matrice di approvazione generale. Vale la prima regola attiva (priorità crescente) per tipo di
 * operazione, importo, settore e utente/ruolo del richiedente; senza regole valgono le soglie predefinite. Attiva solo con
 * il flag «approvazioni.matrice». Le scritture sono dell'Admin (controllo vero sul server).
 */

interface RuleForm {
  operation: string
  minAmount: string
  maxAmount: string
  sectorId: string
  requesterUserId: string
  requesterRole: string
  level: ApprovalLevel
  priority: string
  isActive: boolean
}

const EMPTY_FORM: RuleForm = {
  operation: 'spesa', minAmount: '0', maxAmount: '', sectorId: '', requesterUserId: '', requesterRole: '',
  level: 'Amministrazione', priority: '10', isActive: true,
}

function errMsg(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback
}

function eur(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })
}

const opLabel = (v: string) => APPROVAL_OPERATIONS.find(o => o.value === v)?.label ?? v

export function ApprovalMatrixPage({ auth }: { auth: LoginResponse }) {
  const confirm = useConfirm()
  const canWrite = auth.role === 'Administrator' || auth.role === 'EqualAdmin'
  const [data, setData] = useState<ApprovalMatrixList | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [form, setForm] = useState<RuleForm | null>(null)
  const [editId, setEditId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setData(await approvalMatrixApi.list())
    } catch (e) {
      setError(errMsg(e, 'Impossibile caricare la matrice.'))
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => { void load() }, [load])

  function openNew() {
    setEditId(null)
    setForm({ ...EMPTY_FORM })
    setFormError('')
  }

  function openEdit(r: ApprovalMatrixRule) {
    setEditId(r.id)
    setForm({
      operation: r.operation,
      minAmount: String(r.minAmount),
      maxAmount: r.maxAmount === null ? '' : String(r.maxAmount),
      sectorId: r.sectorId === null ? '' : String(r.sectorId),
      requesterUserId: r.requesterUserId === null ? '' : String(r.requesterUserId),
      requesterRole: r.requesterRole ?? '',
      level: r.level,
      priority: String(r.priority),
      isActive: r.isActive,
    })
    setFormError('')
  }

  async function save() {
    if (!form) return
    const min = Number(form.minAmount.replace(',', '.'))
    const max = form.maxAmount.trim() === '' ? null : Number(form.maxAmount.replace(',', '.'))
    if (Number.isNaN(min) || (max !== null && Number.isNaN(max))) { setFormError('Importi non validi.'); return }
    const body: ApprovalMatrixRuleBody = {
      operation: form.operation,
      minAmount: min,
      maxAmount: max,
      sectorId: form.sectorId ? Number(form.sectorId) : null,
      requesterUserId: form.requesterUserId ? Number(form.requesterUserId) : null,
      requesterRole: form.requesterRole.trim() || null,
      level: form.level,
      priority: Number(form.priority) || 0,
      isActive: form.isActive,
    }
    setSaving(true)
    setFormError('')
    try {
      if (editId === null) await approvalMatrixApi.create(body)
      else await approvalMatrixApi.update(editId, body)
      setForm(null)
      void load()
    } catch (e) {
      setFormError(errMsg(e, 'Errore durante il salvataggio.'))
    } finally {
      setSaving(false)
    }
  }

  async function remove(r: ApprovalMatrixRule) {
    if (!(await confirm({ title: 'Elimina regola', message: `Eliminare la regola per «${opLabel(r.operation)}»?`, confirmLabel: 'Elimina', destructive: true }))) return
    try {
      await approvalMatrixApi.remove(r.id)
      void load()
    } catch (e) {
      setError(errMsg(e, 'Errore durante l’eliminazione.'))
    }
  }

  async function seed() {
    try {
      await approvalMatrixApi.seedDefaults()
      void load()
    } catch (e) {
      setError(errMsg(e, 'Errore durante la creazione delle fasce predefinite.'))
    }
  }

  const items = data?.items ?? []

  return (
    <Page>
      <PageHeader
        title="Matrice di approvazione"
        icon="rule"
        actions={!canWrite ? <StatusPill tone="neutral">Sola lettura</StatusPill> : undefined}
      />
      <div className="d-flex flex-column gap-3">
        <Callout tone="info" role="note">
          Per ogni tipo di operazione la regola attiva con priorità più bassa che combacia decide il livello di chi approva.
          Senza regole valgono le soglie predefinite
          {data ? `: fino a ${eur(data.defaults.responsibleMax)} il responsabile, fino a ${eur(data.defaults.administrationMax)} l’Amministrazione, oltre la Direzione` : ''}.
          La matrice è attiva solo con il flag «approvazioni.matrice» acceso; una regola specifica delle spese ha la precedenza.
        </Callout>
        {error && <Callout tone="error">{error}</Callout>}
        {canWrite && !form && (
          <div className="d-flex gap-2">
            <Button variant="filled" onClick={openNew}>Nuova regola</Button>
            <Button variant="tonal" onClick={() => void seed()}>Crea le fasce predefinite</Button>
          </div>
        )}

        {form && (
          <Card>
            <CardHeader title={editId === null ? 'Nuova regola' : 'Modifica regola'} />
            <CardContent>
              <FormGrid columns={4}>
                <div>
                  <Select label="Operazione" value={form.operation}
                    onChange={e => setForm({ ...form, operation: e.target.value })}>
                    {APPROVAL_OPERATIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Select>
                </div>
                <div>
                  <TextField label="Importo da (€)" inputMode="decimal" value={form.minAmount}
                    onChange={e => setForm({ ...form, minAmount: e.target.value })} />
                </div>
                <div>
                  <TextField label="Importo fino a (€)" inputMode="decimal" placeholder="nessun limite" value={form.maxAmount}
                    onChange={e => setForm({ ...form, maxAmount: e.target.value })} />
                </div>
                <div>
                  <Select label="Livello" value={form.level}
                    onChange={e => setForm({ ...form, level: e.target.value as ApprovalLevel })}>
                    {APPROVAL_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </Select>
                </div>
                <div>
                  <TextField label="Id settore" inputMode="numeric" placeholder="qualsiasi" value={form.sectorId}
                    onChange={e => setForm({ ...form, sectorId: e.target.value.replace(/\D/g, '') })} />
                </div>
                <div>
                  <TextField label="Id richiedente" inputMode="numeric" placeholder="qualsiasi" value={form.requesterUserId}
                    onChange={e => setForm({ ...form, requesterUserId: e.target.value.replace(/\D/g, '') })} />
                </div>
                <div>
                  <TextField label="Ruolo richiedente" maxLength={50} placeholder="qualsiasi" value={form.requesterRole}
                    onChange={e => setForm({ ...form, requesterRole: e.target.value })} />
                </div>
                <div>
                  <TextField label="Priorità" inputMode="numeric" value={form.priority}
                    onChange={e => setForm({ ...form, priority: e.target.value.replace(/[^\d-]/g, '') })} />
                </div>
                <div className="d-flex align-items-end">
                  <Checkbox id="matrix-active" label="Attiva" checked={form.isActive}
                    onChange={e => setForm({ ...form, isActive: e.target.checked })} />
                </div>
              </FormGrid>
              {formError && <div className="md-typescale-body-small text-danger mt-2">{formError}</div>}
              <div className="mt-3 d-flex gap-2">
                <Button variant="text" disabled={saving} onClick={() => setForm(null)}>Annulla</Button>
                <Button variant="tonal" disabled={saving} onClick={() => void save()}>Salva</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <LoadingState />
        ) : items.length === 0 ? (
          <EmptyState icon="inbox" title="Nessuna regola: valgono le soglie predefinite." />
        ) : (
          <DataTable>
            <thead>
              <tr>
                <th>Operazione</th><th>Priorità</th><th>Importo</th><th>Settore</th><th>Richiedente</th>
                <th>Livello</th><th>Stato</th>{canWrite && <th />}
              </tr>
            </thead>
            <tbody>
              {items.map(r => (
                <tr key={r.id} className={r.isActive ? '' : 'text-muted'}>
                  <td>{opLabel(r.operation)}</td>
                  <td>{r.priority}</td>
                  <td className="text-nowrap">{eur(r.minAmount)} – {r.maxAmount === null ? 'senza limite' : eur(r.maxAmount)}</td>
                  <td>{r.sectorId === null ? 'Tutti' : `Settore #${r.sectorId}`}</td>
                  <td>{r.requesterUserId !== null ? `Utente #${r.requesterUserId}` : r.requesterRole ?? 'Chiunque'}</td>
                  <td>{r.level}</td>
                  <td>{r.isActive ? <StatusPill tone="success">Attiva</StatusPill> : <StatusPill tone="neutral">Spenta</StatusPill>}</td>
                  {canWrite && (
                    <td className="text-end text-nowrap">
                      <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => openEdit(r)} />
                      <IconButton icon="delete" size="sm" aria-label="Elimina" onClick={() => void remove(r)} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </div>
    </Page>
  )
}
